/**
 * Stage 1A-d — a worker telling RM24 that the day is not going as planned.
 *
 * Before this, the worker had one way to say "I am late" or "there is no work
 * here": typing free text into the RM24 thread. Staff had to read every
 * message to find the ones that change the day. A report is now a STRUCTURED
 * message in the same `worker_dispatch` thread (`Message.exception`), written
 * by the `reportWorkException` callable — so it still reads as a message, and
 * the kind / order / minutes are machine-readable for the staff views.
 *
 * What a report is NOT (binding, RM24-UX-otsused.md):
 *   - it never changes money, hours, a timesheet or a no-show (B4: a stoppage
 *     is reported, no pay or billing rule is added);
 *   - `missed_day` is a REQUEST to RM24 — no timesheet is created (B3);
 *   - `work_done` is the worker's completion notice on a fixed-price service,
 *     not the client's acceptance and not billing (B2).
 */

/** What the worker is reporting. */
export type WorkExceptionKind =
  /** Running late; `minutesLate` says by how much. */
  | 'late'
  /** Cannot find the site / the entrance. */
  | 'cant_find'
  /** On site, but there is no work to do. */
  | 'no_work'
  /** Work stopped (materials, weather, another trade). Money does not change (B4). */
  | 'stoppage'
  /** Anything else — the note says what. */
  | 'other'
  /** "I worked but did not register" — a request to RM24, not a timesheet (B3). */
  | 'missed_day'
  /** Fixed-price service only: the worker says the work is done (B2). */
  | 'work_done';

export const WORK_EXCEPTION_KINDS: readonly WorkExceptionKind[] = [
  'late',
  'cant_find',
  'no_work',
  'stoppage',
  'other',
  'missed_day',
  'work_done',
];

/** The minutes a worker may pick for `late` (the UI offers these). */
export const WORK_EXCEPTION_LATE_MINUTES = [15, 30, 60] as const;

/**
 * The structured part of a report, stored on the thread message as
 * `Message.exception`. The message `text` carries the same facts in Estonian
 * for anything that only reads text (inbox preview, e-mail).
 */
export interface WorkExceptionDetail {
  kind: WorkExceptionKind;
  jobId: string;
  /** Present for `late`. */
  minutesLate?: number;
  /** The worker's own words, as typed (no machine translation). */
  note?: string;
  /** `missed_day`: the local date the worker says they worked, YYYY-MM-DD. */
  date?: string;
  /** `missed_day`: local wall-clock start, "HH:MM". */
  startTime?: string;
  /** `missed_day`: local wall-clock end, "HH:MM". */
  endTime?: string;
  /**
   * The job's place as staff already knew it from the message text (the
   * order's site address), so the thread can say where without parsing
   * `text`. Server-set; not compared on a retry.
   */
  siteLabel?: string;
  /** The device's stable id for this tap — retries reuse it. */
  clientRequestId: string;
  /** ISO instant the device recorded the tap (may be earlier than receipt when queued). */
  reportedAt?: string;
  /** ISO instant the server received it. */
  receivedAt: string;
}

/** Input of the `reportWorkException` callable. */
export interface ReportWorkExceptionInput {
  kind: WorkExceptionKind;
  jobId: string;
  minutesLate?: number;
  note?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  /** Required: one id per tap, reused on every retry and queued replay. */
  clientRequestId: string;
  /** When the worker tapped (ISO). Absent → the server's receipt time. */
  reportedAt?: string;
  /** The account that queued it — a replay on another account is refused. */
  expectedWorkerId?: string;
}

export interface ReportWorkExceptionResult {
  ok: true;
  conversationId: string;
  messageId: string;
  /** True when this exact request had already been recorded (a retry). */
  replayed: boolean;
}

/**
 * Stage 1A-c (S-T7) — why the approver rejected a working day. A code, so the
 * worker reads it in their own language; the optional free text is shown as
 * the approver wrote it ("kinnitaja kommentaar"), untranslated.
 */
export type TimesheetRejectReasonCode =
  | 'wrong_start'
  | 'wrong_end'
  | 'not_on_site'
  | 'break'
  | 'no_work'
  | 'other';

export const TIMESHEET_REJECT_REASON_CODES: readonly TimesheetRejectReasonCode[] = [
  'wrong_start',
  'wrong_end',
  'not_on_site',
  'break',
  'no_work',
  'other',
];

/** Short alias used by the approval UIs (stage 1A-c). */
export type RejectReasonCode = TimesheetRejectReasonCode;
